import React from 'react';
import { Folder, FolderOpen, InsertDriveFile, ExpandMore, ChevronRight } from '@mui/icons-material';
import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  countFilesInSubtree,
  countSelectedUnderPath,
  addAllFilesInFolder,
} from '../utils/fileUtils';
import type { FileTreeNode, GitFileRef, SelectedFilesState } from '../types';

export interface GitTreeItemProps {
  item: FileTreeNode;
  level?: number;
  path?: string;
  handleFileSelect: (path: string, name: string) => void;
  setSelectedFiles: React.Dispatch<React.SetStateAction<SelectedFilesState>>;
  selectedFiles: SelectedFilesState;
  onFileCheckboxSelect?: (path: string, name: string) => void;
  expandedPaths: Set<string>;
  toggleExpandPath: (path: string) => void;
  disabled?: boolean;
}

export function GitTreeItem({
  item,
  level = 0,
  path = '',
  handleFileSelect,
  setSelectedFiles,
  selectedFiles,
  onFileCheckboxSelect,
  expandedPaths,
  toggleExpandPath,
  disabled = false,
}: GitTreeItemProps) {
  const currentPath = path ? `${path}/${item.name}` : item.name;
  const expanded = expandedPaths.has(currentPath);
  const isFolder = item.type === 'folder';

  let checked = false;
  let indeterminate = false;
  if (isFolder) {
    const totalFiles = countFilesInSubtree(item);
    const selectedCount = countSelectedUnderPath(selectedFiles, currentPath);
    checked = totalFiles > 0 && selectedCount === totalFiles;
    indeterminate = selectedCount > 0 && selectedCount < totalFiles;
  } else {
    checked =
      selectedFiles.files?.some((file) => file.path === path && file.name === item.name) ?? false;
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      if (item.type === 'file') {
        const newFile: GitFileRef = { path, name: item.name };
        setSelectedFiles((prev) => {
          const key = `${newFile.path}|${newFile.name}`;
          const exists = prev.files?.some((f) => `${f.path}|${f.name}` === key);
          return exists ? prev : { ...prev, files: [...(prev.files || []), newFile] };
        });
        onFileCheckboxSelect?.(path, item.name);
      } else if (item.type === 'folder' && item.children) {
        const filesInFolder = addAllFilesInFolder(item, currentPath);
        setSelectedFiles((prev) => {
          const existing = new Set((prev.files || []).map((f) => `${f.path}|${f.name}`));
          const merged = [...(prev.files || [])];
          filesInFolder.forEach((f) => {
            const key = `${f.path}|${f.name}`;
            if (!existing.has(key)) merged.push(f);
          });
          return { ...prev, files: merged };
        });
      }
    } else {
      if (item.type === 'file') {
        setSelectedFiles((prev) => ({
          ...prev,
          files: (prev.files || []).filter(
            (file) => !(file.path === path && file.name === item.name)
          ),
        }));
      } else if (item.type === 'folder') {
        setSelectedFiles((prev) => ({
          ...prev,
          files: (prev.files || []).filter((file) => {
            const full = `${file.path ? file.path + '/' : ''}${file.name}`;
            return !full.startsWith(currentPath + '/');
          }),
        }));
      }
    }
  };

  const handleItemClick = () => {
    if (item.type === 'folder') {
      toggleExpandPath(currentPath);
    } else {
      handleFileSelect(path, item.name);
    }
  };

  return (
    <div>
      <ListItem disablePadding>
        <ListItemButton onClick={handleItemClick} sx={{ pl: level * 2 + 2 }}>
          <Tooltip
            title={disabled ? 'Дождитесь завершения индексации' : ''}
            placement="top"
            arrow
            disableHoverListener={!disabled}
          >
            <span>
              <Checkbox
                checked={checked}
                indeterminate={isFolder ? indeterminate : false}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                size="small"
                disabled={disabled}
              />
            </span>
          </Tooltip>
          <ListItemIcon sx={{ minWidth: 32, opacity: disabled ? 0.5 : 1 }}>
            {item.type === 'folder' ? expanded ? <FolderOpen /> : <Folder /> : <InsertDriveFile />}
          </ListItemIcon>
          <ListItemText primary={item.name} sx={{ opacity: disabled ? 0.5 : 1 }} />
          {item.type === 'folder' && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpandPath(currentPath);
              }}
            >
              {expanded ? <ExpandMore /> : <ChevronRight />}
            </IconButton>
          )}
        </ListItemButton>
      </ListItem>
      {item.type === 'folder' && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children?.map((child, index) => (
              <GitTreeItem
                key={`${currentPath}-${child.name}-${index}`}
                item={child}
                level={level + 1}
                path={currentPath}
                handleFileSelect={handleFileSelect}
                setSelectedFiles={setSelectedFiles}
                selectedFiles={selectedFiles}
                onFileCheckboxSelect={onFileCheckboxSelect}
                expandedPaths={expandedPaths}
                toggleExpandPath={toggleExpandPath}
                disabled={disabled}
              />
            ))}
          </List>
        </Collapse>
      )}
    </div>
  );
}
